import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { ForgotPasswordInput } from '@/types/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Schvalne vagne sdeleni — pres existence emailu nedavame zadnou informaci.
  // Po prvnim odeslani prepneme na "done" stav, at uzivatel nemuze opakovane
  // tlacit submit.
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body: ForgotPasswordInput = { email: email.trim() };
      await api<void>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    } catch {
      // Cilene ignorujeme chybu — backend stejne vraci 204 pro vsechna
      // zadani (i pro neexistujici email). Pripadnou sitove chybu ignorujeme
      // a zobrazime stejnou success message.
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <ClipboardList className="h-7 w-7" />
          </div>
          <CardTitle>Obnova hesla</CardTitle>
          <p className="text-sm text-muted-foreground">
            Zadejte svůj e-mail a my vám pošleme odkaz na obnovu hesla.
          </p>
        </CardHeader>

        <CardContent>
          {done ? (
            <div className="space-y-4">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
                Pokud e-mail existuje, dostanete na něj odkaz pro obnovu hesla.
              </div>
              <Link
                to="/login"
                className="block text-center text-sm text-muted-foreground hover:underline"
              >
                Zpět na přihlášení
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                />
              </div>

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Odeslat reset link'}
              </Button>

              <Link
                to="/login"
                className="block text-center text-sm text-muted-foreground hover:underline"
              >
                Zpět na přihlášení
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
